import { RenderJobStatus } from '../constants'
import { useEffect, useState, type ReactNode, type CSSProperties } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../contexts/AuthContext'
import { templatesApiService } from '../services/templatesApiService'
import { renderApiService } from '../services/renderApiService'
import { usageApiService, type UsageSummary } from '../services/usageApiService'
import { ApiError } from '../services/BaseApiService'
import { shouldOfferBilling, standingHint, standingTone, standingUnit, standingValue, type StandingTone } from '../utils/billingStanding'
import type { Template, RenderJob } from '../types'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'
import { PageHeader, Card, CardHeader } from '../components/ui'

function renderStatusLabel(id: number): string {
  const map: Record<number, string> = { [RenderJobStatus.PENDING]: 'Pending', [RenderJobStatus.PROCESSING]: 'Processing', [RenderJobStatus.COMPLETE]: 'Complete', [RenderJobStatus.FAILED]: 'Failed' }
  return map[id] ?? String(id)
}

function renderStatusColor(id: number): string {
  const map: Record<number, string> = {
    [RenderJobStatus.PENDING]: colors.textSecondary,
    [RenderJobStatus.PROCESSING]: colors.warning,
    [RenderJobStatus.COMPLETE]: colors.success,
    [RenderJobStatus.FAILED]: colors.error,
  }
  return map[id] ?? colors.text
}

function StatCard({ label, value, hint, tone = 'normal', onHintClick }: {
  label: string
  value: string | number
  /** Supporting line under the label — "12 free documents left this month". */
  hint?: string | null
  tone?: StandingTone
  /** Makes the hint actionable. Set only when there is somewhere useful to go. */
  onHintClick?: () => void
}) {
  // Colour is the only thing tone changes: the card keeps its shape whatever a team's standing, so a
  // paying team's dashboard does not lay out differently from a free one's.
  const valueColor = tone === 'blocked' ? colors.error : tone === 'warning' ? colors.warning : colors.primary
  return (
    <div style={{
      background: colors.surface,
      border: `1px solid ${tone === 'blocked' ? colors.errorBorder : colors.border}`,
      borderRadius: radius.md,
      padding: spacing.lg,
      boxShadow: shadow.sm,
    }}>
      <div style={{ fontSize: fontSize['2xl'], fontWeight: 700, color: valueColor, marginBottom: spacing.xs }}>{value}</div>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{label}</div>
      {hint && (onHintClick ? (
        // A team that cannot render needs somewhere to go, not just a number. A button rather than
        // the whole card, so the other stat cards keep behaving identically.
        <button
          type="button"
          onClick={onHintClick}
          style={{
            fontSize: fontSize.xs, color: valueColor, marginTop: spacing.xs,
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            textDecoration: 'underline', font: 'inherit', textAlign: 'left',
          }}
        >
          {hint} — add a card
        </button>
      ) : (
        <div style={{ fontSize: fontSize.xs, color: tone === 'normal' ? colors.textMuted : valueColor, marginTop: spacing.xs }}>
          {hint}
        </div>
      ))}
    </div>
  )
}

function HoverRow({ children, onClick, style }: { children: ReactNode; onClick?: () => void; style?: CSSProperties }) {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        background: hovered ? colors.surfaceMist : 'transparent',
        transition: 'background 120ms ease',
        cursor: onClick ? 'pointer' : undefined,
        borderRadius: radius.sm,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const colHeaderStyle = {
  fontFamily: fontFamily.mono,
  fontSize: fontSize.xs,
  color: colors.textMuted,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  fontWeight: 500,
  paddingBottom: spacing.xs,
}

const viewAllBtnStyle = {
  background: 'none',
  border: 'none',
  color: colors.primary,
  fontSize: fontSize.sm,
  cursor: 'pointer',
  fontWeight: 500,
  padding: 0,
} as const

export default function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<Template[]>([])
  const [jobs, setJobs] = useState<RenderJob[]>([])
  const [usage, setUsage] = useState<UsageSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [templatesError, setTemplatesError] = useState('')
  const [jobsError, setJobsError] = useState('')

  useEffect(() => {
    const message = (err: unknown, fallback: string) => err instanceof ApiError ? err.message : fallback
    Promise.all([
      templatesApiService.list().catch(err => { setTemplatesError(message(err, 'Failed to load templates.')); return [] as Template[] }),
      renderApiService.listJobs().catch(err => { setJobsError(message(err, 'Failed to load render jobs.')); return [] as RenderJob[] }),
      usageApiService.getSummary().catch(() => null),
    ]).then(([t, j, u]) => {
      setTemplates(t)
      setJobs(j)
      setUsage(u)
    }).finally(() => setLoading(false))
  }, [])

  const firstName = user?.firstName || user?.email?.split('@')[0] || 'there'
  const recentJobs = jobs.slice(0, 5)
  // periodStart is midnight UTC on the 1st; read it in UTC or a viewer west of Greenwich sees last month.
  const monthLabel = usage
    ? new Date(usage.periodStart).toLocaleString('default', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    : 'This Month'

  return (
    <div>
      <PageHeader
        eyebrow="DASHBOARD"
        title={`Welcome back, ${firstName}`}
        subtitle="Here's an overview of your document activity."
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: spacing.md,
        marginBottom: spacing.xl,
      }}>
        <StatCard label="Templates" value={loading || templatesError ? '—' : templates.length} />
        <StatCard label="All Render Jobs" value={loading || jobsError ? '—' : jobs.length} />
        <StatCard
          label={`${loading || !usage ? 'Renders' : standingUnit(usage.standing)} (${monthLabel})`}
          value={loading || !usage ? '—' : standingValue(usage.standing, usage.totalRenders)}
          hint={loading || !usage ? null : standingHint(usage.standing)}
          tone={loading || !usage ? 'normal' : standingTone(usage.standing)}
          // Only a free team near or past its allowance has anywhere useful to go. A paying team is
          // metered, so there is nothing to offer them.
          onHintClick={!loading && usage && shouldOfferBilling(usage.standing)
            ? () => navigate('/app/billing')
            : undefined}
        />
        {/* On a page allowance the card above already counts pages; a second Pages card would repeat it. */}
        {!(usage && standingUnit(usage.standing) === 'Pages') && (
          <StatCard
            label={`Pages (${monthLabel})`}
            value={loading || !usage ? '—' : usage.totalPages.toLocaleString()}
          />
        )}
        <StatCard
          label={`Output (${monthLabel})`}
          value={loading || !usage ? '—' : formatBytes(usage.totalBytes)}
        />
      </div>

      <div style={{
        background: colors.primary,
        borderRadius: radius.md,
        padding: spacing.lg,
        marginBottom: spacing.lg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.md,
        flexWrap: 'wrap',
      }}>
        <div>
          {/* "Instant", not "Live" -- "Live" is taken by the Live/Test workspace switcher. */}
          <div style={{ fontWeight: 600, fontSize: fontSize.md, color: colors.onPrimary }}>Instant Preview</div>
          <div style={{ fontSize: fontSize.sm, color: colors.onPrimary, opacity: 0.75, marginTop: '2px' }}>
            Pick a template, fill in values, and see the PDF update instantly.
          </div>
        </div>
        <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
          {templates.length > 0 && (
            <button
              onClick={() => navigate(`/app/templates/${templates[0].templateId}/preview`)}
              style={{
                padding: `${spacing.sm} ${spacing.md}`,
                background: colors.onPrimary,
                color: colors.primary,
                border: 'none',
                borderRadius: radius.full,
                fontSize: fontSize.sm,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Preview a template
            </button>
          )}
          <button
            onClick={() => navigate('/app/templates')}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: 'transparent',
              color: colors.onPrimary,
              border: `1px solid ${colors.onPrimary}`,
              borderRadius: radius.full,
              fontSize: fontSize.sm,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {templates.length === 0 && !templatesError ? 'Upload a template' : 'All templates'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
        <Card>
          <CardHeader
            title="Recent Templates"
            actions={
              <button onClick={() => navigate('/app/templates')} style={viewAllBtnStyle}>
                View all
              </button>
            }
            style={{ marginBottom: spacing.md }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: `0 ${spacing.md}` }}>
            <div style={colHeaderStyle}>Template</div>
            <div style={{ ...colHeaderStyle, textAlign: 'right' }}>Updated</div>
          </div>
          {loading ? (
            <div style={{ color: colors.textSecondary, fontSize: fontSize.base, paddingTop: spacing.sm }}>Loading...</div>
          ) : templatesError ? (
            <div role="alert" style={{ color: colors.error, fontSize: fontSize.base, paddingTop: spacing.sm }}>{templatesError}</div>
          ) : templates.length === 0 ? (
            <div style={{ color: colors.textSecondary, fontSize: fontSize.base, paddingTop: spacing.sm }}>No templates yet.</div>
          ) : (
            templates.slice(0, 5).map(t => (
              <HoverRow
                key={t.templateId}
                onClick={() => navigate(`/app/templates/${t.templateId}`)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: `0 ${spacing.md}`,
                  padding: `${spacing.sm} ${spacing.xs}`,
                  borderTop: `1px solid ${colors.border}`,
                  alignItems: 'center',
                  margin: `0 -${spacing.xs}`,
                }}
              >
                <span style={{ fontSize: fontSize.base, color: colors.text }}>{t.name}</span>
                <span style={{ fontSize: fontSize.sm, color: colors.textSecondary, textAlign: 'right' }}>
                  {new Date(t.updatedAt).toLocaleDateString()}
                </span>
              </HoverRow>
            ))
          )}
        </Card>

        <Card>
          <CardHeader
            title="Recent Render Jobs"
            actions={
              <button onClick={() => navigate('/app/render-jobs')} style={viewAllBtnStyle}>
                View all
              </button>
            }
            style={{ marginBottom: spacing.md }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: `0 ${spacing.md}` }}>
            <div style={colHeaderStyle}>Status</div>
            <div style={{ ...colHeaderStyle, textAlign: 'right' }}>Duration</div>
            <div style={{ ...colHeaderStyle, textAlign: 'right' }}>Date</div>
          </div>
          {loading ? (
            <div style={{ color: colors.textSecondary, fontSize: fontSize.base, paddingTop: spacing.sm }}>Loading...</div>
          ) : jobsError ? (
            <div role="alert" style={{ color: colors.error, fontSize: fontSize.base, paddingTop: spacing.sm }}>{jobsError}</div>
          ) : recentJobs.length === 0 ? (
            <div style={{ color: colors.textSecondary, fontSize: fontSize.base, paddingTop: spacing.sm }}>No render jobs yet.</div>
          ) : (
            recentJobs.map(j => (
              <HoverRow
                key={j.renderJobId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: `0 ${spacing.md}`,
                  padding: `${spacing.sm} ${spacing.xs}`,
                  borderTop: `1px solid ${colors.border}`,
                  alignItems: 'center',
                  margin: `0 -${spacing.xs}`,
                }}
              >
                <span style={{ fontSize: fontSize.sm, color: renderStatusColor(j.renderStatusLookupId), fontWeight: 500 }}>
                  {renderStatusLabel(j.renderStatusLookupId)}
                </span>
                <span style={{ fontSize: fontSize.sm, color: colors.textSecondary, textAlign: 'right' }}>
                  {j.durationMs != null ? `${j.durationMs}ms` : '—'}
                </span>
                <span style={{ fontSize: fontSize.sm, color: colors.textSecondary, textAlign: 'right' }}>
                  {new Date(j.createdAt).toLocaleDateString()}
                </span>
              </HoverRow>
            ))
          )}
        </Card>
      </div>
    </div>
  )
}
